define((require) => {
    "use strict";

    var LoggedIn            = require("shared/security/LoggedIn"),
        Permissions         = require("shared/security/Permissions"),
        CompanyState        = require("shared/data/CompanyState"),
        ConfirmationManager = require("services/ConfirmationManager"),
        Cookie              = require("editor/util/Cookie"),
        ErrorHandler        = require("shared/services/error/ErrorHandler"),
        NotificationBar     = require("editor/ui/notificationbar/NotificationBar");

    var TAG = "restricted_";

    var timer = null;

    /**
     * @private
     * Return the notification options for notifications about principal restrictions.
     *
     * @param  {String}            tag The notification tag to postfix TAG; never null.
     * @param  {Number} closeDelay the delay introduced before closing the notification bar
     * @return {*} The notification options for notifications about user restrictions; never null.
     */
    function options(tag, closeDelay) {
        return {
            tag:        TAG + tag,
            queue:      false,
            closeDelay: closeDelay || null,
            flash:      {duplicate: false}
        };
    }

    /**
     * @private
     * Closes a specific or all notifications regarding login restrictions...
     *
     * @param  {String} [tag] The specific notification tag to postfix TAG, if any; if falsey, all notifications
     *                        prefixed with TAG will be closed.
     */
    function close(tag) {
        NotificationBar.close({tag: TAG + (tag || ":")});
    }

    /**
     * @private
     * Notify the user about company restrictions, such as login restricted (AD login only) or about content
     * restrictions. <p>
     *
     * This function will not prevent <tt>principal</tt> from logging in (will already be prevented, if so).
     *
     * @param  {Principal} principal The principal to check and notify for company restrictions; never null.
     * @return {Boolean} True if not notified, false otherwise.
     */
    function handleCompanyRestrictions(principal) {
        var state = principal.get("state"),
            updated = principal.get("updated");

        if (state === CompanyState.LOGIN_RESTRICTED || state === CompanyState.SUSPENDED) { // From login page, or via AD login...
            NotificationBar.error("You cannot currently sign in because your company account appears to be suspended. Please reach out to your Account Manager to regain access.", options("login"));
            return false;

        // Only relevant for non-Spectator users backed by the logged in principal...
        } else if (state === CompanyState.CONTENT_RESTRICTED && Permissions.Roles.SPECTATOR.not(principal.get("user"))) { // user role!
            var action = [ErrorHandler.Actions.COMMUNITY, ErrorHandler.Actions.SUPPORT];  //  "community support";
            // Only show this one time, not on updated principal because of relogin...
            if (updated === null) {
                action.push(ErrorHandler.Actions.RELOGIN);
            }

            NotificationBar.open({message: "You are currently in Spectator mode because your company account appears to be suspended. Please reach out to your Account Manager to regain access.", action: action.join(" ")}, options("content"));
            return false;

        // If (now) active, close all restrictions. This is fine to do here, since we have not checked for read only
        // tokens yet, i.e. may be notified again...
        } else if (state === CompanyState.ACTIVE) {
            close();
        }

        // Notify the user on role and/or status change, e.g. via re-login...
        if (updated !== null) {
            var msg = "Your active access level ";

            msg += updated ? ("is now " + principal.get("role")) : "has not changed";
            msg += ".";

            NotificationBar.open(msg, options("relogin", 7500));

            return false;
        }

        return true;
    }

    /**
     * @private
     * Notify the principal that the token is currently read only since old token, so a soft re-login will be
     * possible. However, we only bother doing this in case the *user* is not a Spectator, i.e. then no difference
     * between Normal and ReadOnly tokens. <p>
     *
     * This function will not prevent <tt>principal</tt> from logging in!
     *
     * @param  {Principal} principal The principal to check and notify for read only token; never null.
     * @return {Boolean} True if not notified, false otherwise.
     */
    function handleReadOnly(principal) {
        // Ignore this message if the user is not confirmed, since that is handled by the ConfirmationManager: as long
        // as unconfirmed, we do not care what type of token, as the user cannot do anything (but Spectator stuff)
        // until confirmed...
        var user = principal.get("user"),
            role = user.get("role"); // granted - but perhaps not applicable - user role!

        if (principal.get("type") === "ReadOnly" && user.get("confirmedAt") && Permissions.Roles.SPECTATOR.not(role) && !Cookie.get("lockout:" + user.get("username"))) {
            NotificationBar.open("You are currently in Spectator mode, and you need to sign in again to use your " + role.name + " role.&nbsp;&nbsp;&nbsp;<a href='#login'>Sign In Again</a>&nbsp;&nbsp;&nbsp;<a href='#user/forgotpassword'>Forgot password?</a>", options("read_only"));
            return false;
        }

        close("read_only");

        return true;
    }

    /**
     * @private
     * Silently sends a confirmation email to the principal if the associated user has not had one sent yet.
     *
     * @param  {Principal} principal The principal to send a confirmation email if applicable; never null.
     * @return {Boolean} True if no email sent, false otherwise.
     */
    function handleUnconfirmed(principal) {
        // Silently request confirm email from server if needed...
        var user = principal.get("user"); // testing of a user is akin to asking principal.canLogin()

        // Do not send a confirmation info request if this is an AD login
        if (principal.get("type") === "AD") {
            return true;
        }

        if (user && !user.get("lastConfirmationEmailSent") && ConfirmationManager.confirmationRequired(user)) {
            ConfirmationManager.requestConfirmationInfo(false); // false = silent

            return false;
        }

        return true;
    }

    // Make sure we are notified on changes when the principal changes...
    LoggedIn.on("change:login error:login_restricted", (principal) => {
        if (principal) {
            // Make sure we send a confirmation email to the associated user, if none has been set yet...
            handleUnconfirmed(principal);
            // Make sure the principal (user) is notified about any company restrictions. If applicable here, since we
            // have a token, we know we can login, so it must be the published content that is restricted. If we notify
            // the user about company restrictions, do not bother notify her about a potential read only token...
            if (handleCompanyRestrictions(principal)) {
                // Make sure the principal (user) gets an option to login again softly on old read-only tokens by a nice
                // login presented via a notification message. In addition, the settings menu will also reflect this, so
                // even if the notification is closed, the user still has an easy option to sign in again to upgrade to
                // a normal token...
                handleReadOnly(principal);
            }
        } else {
            close();
        }
    });

    // Make sure we are notified on changes on too many attempts to log in, either first time, or on re-login when
    // trying to upgrade a principal read only token to a normal one...
    LoggedIn.on("login:tooManyAttempts", (principal, lockout) => { // lockout in seconds!
        if (principal && principal.get("type") === "ReadOnly") {
            close("read_only");

            if (timer) {
                clearTimeout(timer);
            }

            timer = setTimeout(() => {
                timer = null;
                // Must be same principal logged in for this to make sense...
                if (LoggedIn.principal() === principal) {
                    handleReadOnly(principal);
                }
            }, lockout * 1000);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/security/LoggedInNotifier.js